import React, { useEffect, useState } from 'react';
import BrandsSection from '../components/BrandsSection';
import CoinCouponsSection from '../components/CoinCouponsSection';
import CoinsBulkSection from '../components/CoinsBulkSection';
import CoinsDeductSection from '../components/CoinsDeductSection';
import HomeAdEditor from '../components/HomeAdEditor';
import ZocsSyncSection from '../components/ZocsSyncSection';
import {
  createFeatureFlag,
  createHomeAd,
  decideRewardClaim,
  getCoupons,
  getFeatureFlags,
  getHomeAds,
  getRewardClaims,
  notifyPhones,
  reorderHomeAds,
  updateCoupon,
  updateFeatureFlag,
  updateHomeAd,
} from '../api/skipzyAdmin';

const CLAIM_TYPE_LABEL = {
  instagram_follow: 'Instagram Follow',
  occasion_share: 'Occasion Share',
};

function errorMessage(err, fallback) {
  const detail = err.response?.data?.detail;
  if (!detail) return err.message || fallback;
  if (typeof detail === 'string') return detail;
  if (detail.message) return typeof detail.message === 'string' ? detail.message : JSON.stringify(detail.message);
  return JSON.stringify(detail);
}

// Same sub-tab pattern as BrandOps.jsx — one tab per settings section.
const TABS = [
  { key: 'flags',   label: 'Feature Flags' },
  { key: 'coupons', label: 'Coupons' },
  { key: 'coincoupons', label: 'Coin Coupons' },
  { key: 'brands',  label: 'Brands' },
  { key: 'ads',     label: 'Home Ads' },
  { key: 'claims',  label: 'Reward Claims' },
  { key: 'notify',  label: 'Notify & Gift Coins' },
  { key: 'zocssync', label: 'ZOCS Sync' },
  { key: 'coinsbulk', label: 'Bulk Coins' },
  { key: 'coinsdeduct', label: 'Coins Deduct' },
];

export default function SkipzyAppSettings() {
  const [flags, setFlags] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [ads, setAds] = useState([]);
  const [claims, setClaims] = useState([]);
  // null = closed, {} = creating, an ad = editing that one.
  const [editingAd, setEditingAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newFlag, setNewFlag] = useState({ key: '', description: '' });
  const [addingFlag, setAddingFlag] = useState(false);
  const [notify, setNotify] = useState({ phones: '', message: '', coins: '', expiry_days: '' });
  const [notifying, setNotifying] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);
  const [tab, setTab] = useState(TABS[0].key);

  // Comma / space / newline separated; keeps only the last 10 digits so
  // "+91 98765 43210" pastes cleanly.
  const parsedPhones = notify.phones
    .split(/[\s,;]+/)
    .map(p => p.replace(/\D/g, '').slice(-10))
    .filter(Boolean);

  async function sendNotify(e) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setNotifyResult(null);
    const coins = notify.coins ? parseInt(notify.coins, 10) : null;
    const expiry = notify.expiry_days ? parseInt(notify.expiry_days, 10) : null;
    if (coins && !expiry) return setError('Expiry (days) is required when coins are set.');
    if (!coins && expiry) return setError('Expiry only applies when coins are set.');
    const summary = coins
      ? `Send to ${parsedPhones.length} number(s) and credit ${coins} coins each, expiring in ${expiry} days?`
      : `Send this message to ${parsedPhones.length} number(s)?`;
    // Coin grants aren't idempotent, so a second click would credit twice.
    if (!window.confirm(summary)) return;
    setNotifying(true);
    try {
      const body = { phones: parsedPhones, message: notify.message.trim() };
      if (coins) { body.coins = coins; body.expiry_days = expiry; }
      const res = await notifyPhones(body);
      setNotifyResult(res);
      setSuccess(`Notified ${res.notified_count} of ${res.results.length} number(s).`);
    } catch (err) {
      setError(errorMessage(err, 'Could not send the notification'));
    } finally {
      setNotifying(false);
    }
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [flagRows, couponRows, adRows, claimRows] = await Promise.all([
        getFeatureFlags(), getCoupons(), getHomeAds(), getRewardClaims(),
      ]);
      setFlags(flagRows || []);
      setCoupons(couponRows || []);
      setAds(adRows || []);
      // Newest first, but pending ones always float to the top — that's the
      // queue someone actually has to act on.
      setClaims(
        (claimRows || []).slice().sort((a, b) => {
          if (a.status === 'pending' && b.status !== 'pending') return -1;
          if (b.status === 'pending' && a.status !== 'pending') return 1;
          return new Date(b.submitted_at) - new Date(a.submitted_at);
        })
      );
    } catch (err) {
      setError(errorMessage(err, 'Failed to load Skipzy settings'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function toggleFlag(flag) {
    setBusyKey(`flag:${flag.key}`);
    setError(null);
    setSuccess(null);
    try {
      await updateFeatureFlag(flag.key, { enabled: !flag.enabled });
      setSuccess(`${flag.key} ${!flag.enabled ? 'enabled' : 'disabled'}.`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Flag update failed'));
    } finally {
      setBusyKey(null);
    }
  }

  async function addFlag(e) {
    e.preventDefault();
    if (!newFlag.key.trim()) return;
    setAddingFlag(true);
    setError(null);
    setSuccess(null);
    try {
      await createFeatureFlag({
        key: newFlag.key.trim(),
        enabled: false,
        description: newFlag.description.trim() || null,
      });
      setSuccess(`${newFlag.key.trim()} created (off by default).`);
      setNewFlag({ key: '', description: '' });
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not create flag'));
    } finally {
      setAddingFlag(false);
    }
  }

  async function toggleCoupon(coupon) {
    setBusyKey(`coupon:${coupon.code}`);
    setError(null);
    setSuccess(null);
    const next = !coupon.is_active;
    if (next === false && !window.confirm(`Deactivate ${coupon.code}? It will stop working immediately, everywhere.`)) {
      setBusyKey(null);
      return;
    }
    try {
      await updateCoupon(coupon.code, { is_active: next });
      setSuccess(`${coupon.code} ${next ? 'activated' : 'deactivated'}.`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Coupon update failed'));
    } finally {
      setBusyKey(null);
    }
  }

  async function decideClaim(claim, approved) {
    let reject_reason;
    if (!approved) {
      reject_reason = window.prompt(
        `Why is ${claim.customer_phone}'s ${CLAIM_TYPE_LABEL[claim.claim_type]} claim being rejected? ` +
          'This is shown to the customer.'
      );
      if (reject_reason === null) return; // cancelled
    }
    setBusyKey(`claim:${claim.id}`);
    setError(null);
    setSuccess(null);
    try {
      await decideRewardClaim(claim.id, { approved, reject_reason: reject_reason || undefined });
      setSuccess(
        approved
          ? `Approved. ${claim.coins} SC credited to ${claim.customer_phone}.`
          : `Rejected ${claim.customer_phone}'s claim.`
      );
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not record that decision'));
    } finally {
      setBusyKey(null);
    }
  }

  // Home ads. Every write here lands on the app's Home tab straight away —
  // the Skipzy backend drops its feed cache on each one — so the confirms and
  // the reload-after-write matter more than they do above.
  async function saveAd(payload, slug) {
    if (slug) await updateHomeAd(slug, payload);
    else await createHomeAd(payload);
    setEditingAd(null);
    setSuccess(`${slug || payload.slug} saved. It is live on Home now.`);
    setError(null);
    await load();
  }

  async function toggleAd(ad) {
    const next = !ad.is_active;
    if (next === false && !window.confirm(`Deactivate ${ad.slug}? The card leaves the Home feed immediately for everyone.`)) {
      return;
    }
    setBusyKey(`ad:${ad.slug}`);
    setError(null);
    setSuccess(null);
    try {
      await updateHomeAd(ad.slug, { is_active: next });
      setSuccess(`${ad.slug} ${next ? 'is back on Home' : 'pulled off Home'}.`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Home ad update failed'));
    } finally {
      setBusyKey(null);
    }
  }

  async function moveAd(index, delta) {
    const target = index + delta;
    if (target < 0 || target >= ads.length) return;
    // The whole order goes up as one list: a per-row display_order would leave
    // the feed half-reordered if the second call failed.
    const slugs = ads.map(a => a.slug);
    [slugs[index], slugs[target]] = [slugs[target], slugs[index]];
    setBusyKey(`ad:${ads[index].slug}`);
    setError(null);
    setSuccess(null);
    try {
      await reorderHomeAds(slugs);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not reorder the feed'));
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div>
      <div className="bg-white border-b border-gray-200 px-6 pt-3 flex items-center gap-1">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold rounded-t-md transition-colors ${
              tab === t.key
                ? 'text-zoddle-green border-b-2 border-zoddle-green'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="p-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Skipzy App Settings</h2>
          <p className="text-sm text-gray-400 mt-1">
            Home ads, feature flags, brands and coupons — live on the app and at checkout the moment
            you save. No rebuild, no deploy.
          </p>
        </div>
        <button
          onClick={load}
          className="text-sm font-semibold text-gray-500 hover:text-zoddle-green transition-colors"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-100 text-zoddle-pink text-sm px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-100 text-green-700 text-sm px-4 py-3 rounded">
          {success}
        </div>
      )}

      {loading && <div className="text-gray-500">Loading...</div>}

      {!loading && (
        <>
          {tab === 'flags' && (
          <section className="mb-8">
            <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">Feature Flags</h3>
            <form onSubmit={addFlag} className="flex items-end gap-2 mb-4">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">New flag key</label>
                <input
                  value={newFlag.key}
                  onChange={e => setNewFlag(f => ({ ...f, key: e.target.value }))}
                  placeholder="e.g. scan_to_pay_enabled"
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Description (optional)</label>
                <input
                  value={newFlag.description}
                  onChange={e => setNewFlag(f => ({ ...f, description: e.target.value }))}
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm"
                />
              </div>
              <button
                type="submit"
                disabled={addingFlag || !newFlag.key.trim()}
                className="text-sm font-semibold bg-zoddle-green text-white px-3 py-2 rounded hover:opacity-90 disabled:opacity-50"
              >
                Add flag
              </button>
            </form>
            <div className="bg-white border border-gray-200 rounded overflow-hidden mb-3">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2">Key</th>
                    <th className="text-left px-4 py-2">Description</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-right px-4 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {flags.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-3 text-gray-400 text-sm">No flags yet.</td></tr>
                  )}
                  {flags.map(flag => (
                    <tr key={flag.key} className="border-t border-gray-100">
                      <td className="px-4 py-3 font-mono text-gray-800">{flag.key}</td>
                      <td className="px-4 py-3 text-gray-600">{flag.description || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${flag.enabled ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {flag.enabled ? 'ON' : 'OFF'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => toggleFlag(flag)}
                          disabled={busyKey === `flag:${flag.key}`}
                          className={`text-sm font-semibold px-3 py-1.5 rounded disabled:opacity-50 ${
                            flag.enabled
                              ? 'border border-zoddle-pink text-zoddle-pink hover:bg-red-50'
                              : 'bg-zoddle-green text-white hover:opacity-90'
                          }`}
                        >
                          {flag.enabled ? 'Turn off' : 'Turn on'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          )}
          {tab === 'coupons' && (
          <section className="mb-8">
            <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">Coupons</h3>
            <div className="bg-white border border-gray-200 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2">Code</th>
                    <th className="text-left px-4 py-2">Category</th>
                    <th className="text-left px-4 py-2">Aliases</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-right px-4 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-3 text-gray-400 text-sm">No coupons yet.</td></tr>
                  )}
                  {coupons.map(coupon => (
                    <tr key={coupon.code} className="border-t border-gray-100">
                      <td className="px-4 py-3 font-mono text-gray-800">{coupon.code}</td>
                      <td className="px-4 py-3 text-gray-600">{coupon.category}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {(coupon.aliases || []).length > 0 ? `${coupon.aliases.length} alias(es)` : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${coupon.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {coupon.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => toggleCoupon(coupon)}
                          disabled={busyKey === `coupon:${coupon.code}`}
                          className={`text-sm font-semibold px-3 py-1.5 rounded disabled:opacity-50 ${
                            coupon.is_active
                              ? 'border border-zoddle-pink text-zoddle-pink hover:bg-red-50'
                              : 'bg-zoddle-green text-white hover:opacity-90'
                          }`}
                        >
                          {coupon.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          )}
          {tab === 'coincoupons' && <CoinCouponsSection />}
          {tab === 'brands' && <BrandsSection />}
          {tab === 'zocssync' && <ZocsSyncSection />}
          {tab === 'coinsbulk' && <CoinsBulkSection />}
          {tab === 'coinsdeduct' && <CoinsDeductSection />}
          {tab === 'ads' && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wide">Home Ads</h3>
              <button
                onClick={() => setEditingAd({})}
                className="text-sm font-semibold bg-zoddle-green text-white px-3 py-1.5 rounded hover:opacity-90"
              >
                + New ad
              </button>
            </div>
            <div className="bg-white border border-gray-200 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2 w-16">Order</th>
                    <th className="text-left px-4 py-2 w-20">Hero</th>
                    <th className="text-left px-4 py-2">Slug</th>
                    <th className="text-left px-4 py-2">Name / Headline</th>
                    <th className="text-left px-4 py-2">Width</th>
                    <th className="text-left px-4 py-2">Genders</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-right px-4 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {ads.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-3 text-gray-400 text-sm">No home ads yet.</td></tr>
                  )}
                  {ads.map((ad, i) => (
                    <tr key={ad.slug} className="border-t border-gray-100">
                      <td className="px-4 py-3 text-gray-400">
                        <button
                          onClick={() => moveAd(i, -1)}
                          disabled={i === 0 || busyKey === `ad:${ad.slug}`}
                          className="disabled:opacity-20 hover:text-zoddle-green px-1"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => moveAd(i, 1)}
                          disabled={i === ads.length - 1 || busyKey === `ad:${ad.slug}`}
                          className="disabled:opacity-20 hover:text-zoddle-green px-1"
                        >
                          ↓
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        {ad.hero_image ? (
                          <img
                            src={ad.hero_image}
                            alt=""
                            className="w-10 h-12 object-cover rounded border border-gray-200"
                          />
                        ) : (
                          <span className="text-gray-300 text-xs">none</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-gray-800">{ad.slug}</td>
                      <td className="px-4 py-3 text-gray-600">
                        <div className="font-semibold text-gray-800">{ad.header_text}</div>
                        {ad.name !== ad.header_text && (
                          <div className="text-xs text-gray-400">{ad.name}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{ad.width}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {(ad.genders || []).join(' · ') || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${ad.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {ad.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => setEditingAd(ad)}
                          className="text-sm font-semibold text-gray-500 hover:text-zoddle-green px-2"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => toggleAd(ad)}
                          disabled={busyKey === `ad:${ad.slug}`}
                          className={`text-sm font-semibold px-3 py-1.5 rounded disabled:opacity-50 ${
                            ad.is_active
                              ? 'border border-zoddle-pink text-zoddle-pink hover:bg-red-50'
                              : 'bg-zoddle-green text-white hover:opacity-90'
                          }`}
                        >
                          {ad.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          )}
          {tab === 'claims' && (
          <section>
            <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">
              Reward Claims
            </h3>
            <p className="text-sm text-gray-400 mb-3">
              Instagram follows and occasion shares can't be verified automatically, so the
              customer submits a screenshot and it lands here. Approving credits their coins
              immediately; rejecting tells them why.
            </p>
            <div className="bg-white border border-gray-200 rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2 w-20">Proof</th>
                    <th className="text-left px-4 py-2">Customer</th>
                    <th className="text-left px-4 py-2">Claim</th>
                    <th className="text-left px-4 py-2">Coins</th>
                    <th className="text-left px-4 py-2">Submitted</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-right px-4 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-3 text-gray-400 text-sm">No claims yet.</td></tr>
                  )}
                  {claims.map(claim => (
                    <tr key={claim.id} className="border-t border-gray-100 align-top">
                      <td className="px-4 py-3">
                        <a href={claim.proof_url} target="_blank" rel="noreferrer">
                          <img
                            src={claim.proof_url}
                            alt="Submitted proof"
                            className="w-14 h-14 object-cover rounded border border-gray-200"
                          />
                        </a>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <div className="font-semibold text-gray-800">
                          {claim.customer_name || claim.customer_phone}
                        </div>
                        {claim.customer_name && (
                          <div className="text-xs text-gray-400">{claim.customer_phone}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {CLAIM_TYPE_LABEL[claim.claim_type] || claim.claim_type}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{claim.coins} SC</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {new Date(claim.submitted_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-xs font-semibold px-2 py-1 rounded-full ${
                            claim.status === 'approved'
                              ? 'bg-green-50 text-green-700'
                              : claim.status === 'rejected'
                                ? 'bg-red-50 text-zoddle-pink'
                                : 'bg-amber-50 text-amber-700'
                          }`}
                        >
                          {claim.status.toUpperCase()}
                        </span>
                        {claim.status === 'rejected' && claim.reject_reason && (
                          <div className="text-xs text-gray-400 mt-1 max-w-[16rem]">
                            {claim.reject_reason}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {claim.status === 'pending' ? (
                          <>
                            <button
                              onClick={() => decideClaim(claim, true)}
                              disabled={busyKey === `claim:${claim.id}`}
                              className="text-sm font-semibold bg-zoddle-green text-white px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-50 mr-2"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => decideClaim(claim, false)}
                              disabled={busyKey === `claim:${claim.id}`}
                              className="text-sm font-semibold border border-zoddle-pink text-zoddle-pink px-3 py-1.5 rounded hover:bg-red-50 disabled:opacity-50"
                            >
                              Reject
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">
                            {claim.reviewed_by ? `by ${claim.reviewed_by}` : ''}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          )}
          {tab === 'notify' && (
          <section>
            <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-3">
              Notify &amp; Gift Coins
            </h3>
            <p className="text-sm text-gray-400 mb-3">
              Sends a push to the numbers below. Add coins and an expiry to also credit each
              wallet — your message is sent exactly as written, so mention the coins and expiry in it yourself. Numbers
              with no Skipzy account are skipped and listed below.
            </p>
            <form onSubmit={sendNotify} className="bg-white border border-gray-200 rounded p-4 space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Phone numbers (10-digit, one per line or comma separated, max 500)
                </label>
                <textarea
                  value={notify.phones}
                  onChange={e => setNotify(n => ({ ...n, phones: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm font-mono"
                />
                <div className="text-xs text-gray-400 mt-1">{parsedPhones.length} number(s)</div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Message</label>
                <textarea
                  value={notify.message}
                  onChange={e => setNotify(n => ({ ...n, message: e.target.value }))}
                  rows={2}
                  maxLength={500}
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm"
                />
              </div>
              <div className="flex gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Coins (optional)</label>
                  <input
                    type="number"
                    min="1"
                    value={notify.coins}
                    onChange={e => setNotify(n => ({ ...n, coins: e.target.value }))}
                    className="w-32 border border-gray-200 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Expiry (days)</label>
                  <input
                    type="number"
                    min="1"
                    value={notify.expiry_days}
                    onChange={e => setNotify(n => ({ ...n, expiry_days: e.target.value }))}
                    className="w-32 border border-gray-200 rounded px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={notifying || parsedPhones.length === 0 || parsedPhones.length > 500 || !notify.message.trim()}
                className="text-sm font-semibold bg-zoddle-green text-white px-4 py-2 rounded hover:opacity-90 disabled:opacity-50"
              >
                {notifying ? 'Sending...' : 'Send'}
              </button>
            </form>
            {notifyResult && notifyResult.results.some(r => !r.notified) && (
              <div className="mt-3 bg-white border border-gray-200 rounded p-3 text-sm">
                <div className="font-semibold text-gray-700 mb-1">Skipped</div>
                {notifyResult.results.filter(r => !r.notified).map(r => (
                  <div key={r.phone} className="text-gray-500">
                    <span className="font-mono">{r.phone}</span> — {r.reason || 'not notified'}
                  </div>
                ))}
              </div>
            )}
          </section>
          )}
        </>
      )}

      {editingAd && (
        <HomeAdEditor
          ad={editingAd.slug ? editingAd : null}
          onClose={() => setEditingAd(null)}
          onSave={saveAd}
        />
      )}
      </div>
    </div>
  );
}
